import { importLibrary, setOptions } from '@googlemaps/js-api-loader';
import { runtimeConfig } from '@/constants';
import type { PlaceProfile } from '@/types/google-api';

const TIMEOUT_MS = 8000;

class GoogleApiService {
	private placesService: google.maps.places.PlacesService | null = null;
	private initialized = false;

	private init(): void {
		if (!this.initialized) {
			setOptions({
				key: runtimeConfig.googleApiKey,
				libraries: ['places']
			});
			this.initialized = true;
		}
	}

	async getPlacesService(): Promise<google.maps.places.PlacesService> {
		this.init();

		if (!this.placesService) {
			await importLibrary('places');
			this.placesService = new google.maps.places.PlacesService(
				document.createElement('div')
			);
		}

		return this.placesService;
	}

	private buildProfile(
		place: google.maps.places.PlaceResult,
		placeId: string
	): PlaceProfile {
		let photoUrl = '';
		if (place.photos && place.photos.length > 0) {
			photoUrl = place.photos[0].getUrl({ maxWidth: 800 });
		}
		return {
			name: place.name ?? '',
			address: place.formatted_address ?? '',
			phone: place.formatted_phone_number ?? '',
			website: place.website ?? '',
			photoUrl,
			rating: place.rating,
			totalRatings: place.user_ratings_total,
			placeId
		};
	}

	async getPlaceDetails(placeId: string): Promise<PlaceProfile | null> {
		const service = await this.getPlacesService();

		return new Promise((resolve) => {
			const timeout = setTimeout(() => {
				resolve(null);
			}, TIMEOUT_MS);

			service.getDetails(
				{
					placeId,
					fields: [
						'name',
						'formatted_address',
						'formatted_phone_number',
						'website',
						'rating',
						'user_ratings_total',
						'photos'
					]
				},
				(place, status) => {
					clearTimeout(timeout);
					if (status !== google.maps.places.PlacesServiceStatus.OK || !place) {
						resolve(null);
						return;
					}
					resolve(this.buildProfile(place, placeId));
				}
			);
		});
	}

	async findPlaceByQuery(query: string): Promise<PlaceProfile | null> {
		const service = await this.getPlacesService();

		return new Promise((resolve) => {
			const timeout = setTimeout(() => {
				resolve(null);
			}, TIMEOUT_MS);

			service.findPlaceFromQuery(
				{ query, fields: ['place_id', 'name', 'formatted_address'] },
				(results, status) => {
					if (
						status !== google.maps.places.PlacesServiceStatus.OK ||
						!results ||
						results.length === 0
					) {
						clearTimeout(timeout);
						resolve(null);
						return;
					}

					const placeId = results[0].place_id;
					if (!placeId) {
						clearTimeout(timeout);
						resolve(null);
						return;
					}

					clearTimeout(timeout);
					this.getPlaceDetails(placeId).then(resolve);
				}
			);
		});
	}
}

export type { PlaceProfile };
export const googleApiService = new GoogleApiService();
